<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { Chart, type ChartConfiguration } from 'chart.js/auto';
  import { db, type SessionRecord } from '../lib/db';
  import MiniQuadra from '../lib/court/MiniQuadra.svelte';
  import ReplayBlock from '../lib/court/ReplayBlock.svelte';
  import { deterministicImpact, hashString } from '../lib/court/geometry';
  import type { CourtImpact } from '../lib/court/types';
  import { getCoachingFeedback } from '../lib/coachingAdapter';
  import Scoreboard from '../lib/match/Scoreboard.svelte';
  import { emptyMatchState, type MatchState } from '../lib/match/matchState';

  const { params } = $props<{ params: { id: string } }>();
  let session = $state<SessionRecord | null>(null);
  let videoUrl = $state<string | null>(null);
  let selectedSwing = $state(0);
  let chartCanvas = $state<HTMLCanvasElement | null>(null);
  let chart: Chart | null = null;
  let matchState = $state<MatchState>(emptyMatchState());

  onMount(async () => {
    session = (await db.sessions.get(params.id)) ?? null;
    if (session) videoUrl = URL.createObjectURL(session.videoBlob);
  });

  onDestroy(() => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    chart?.destroy();
  });

  // Mocked Sprint 2 data: one CourtImpact per swing, seeded deterministically
  // by (session id hash + swing index). Real event-detector output will
  // replace this once ball tracking lands in Sprint 3.
  const impacts = $derived.by<CourtImpact[]>(() => {
    if (!session) return [];
    const base = hashString(session.id);
    return session.swings.map((sw, i) => {
      const speed = Math.round(60 + sw.peakWristAngularVelocityRadPerSec * 6);
      const player: 1 | 2 = 1;
      return deterministicImpact(base + i * 101, player, sw.shotType ?? 'Forehand', speed);
    });
  });

  const feedback = $derived(
    session
      ? getCoachingFeedback(session.swings[selectedSwing], selectedSwing, session.swings)
      : { tips: [] },
  );

  const currentImpactForMiniQuadra = $derived.by(() => {
    const cur = impacts[selectedSwing];
    if (!cur) return null;
    return { player: cur.player, zone: cur.zone, speed: cur.speed, shotType: cur.shotType };
  });

  // Contact times paired 1:1 with `impacts` — fed to ReplayBlock so the
  // mini-court only reveals impacts as the video playhead crosses them.
  const contactTimesMs = $derived.by<number[]>(() =>
    session ? session.swings.map((sw) => sw.contactTimeMs) : [],
  );

  // A session has a usable replay only when we captured a live timeline
  // during analysis AND saved the source video blob. Pre-Sprint-3 sessions
  // lack the timeline and fall back to the static video + mini-court layout.
  const hasReplay = $derived(
    !!session && !!videoUrl && !!session.courtTimeline && session.courtTimeline.length > 0,
  );

  $effect(() => {
    if (!session || !chartCanvas) return;
    const swing = session.swings[selectedSwing];
    if (!swing) return;

    chart?.destroy();
    const cfg: ChartConfiguration = {
      type: 'line',
      data: {
        labels: swing.elbowAngleSeriesDeg.map((_, i) => i),
        datasets: [
          {
            label: 'Ângulo do cotovelo (°)',
            data: [...swing.elbowAngleSeriesDeg],
            borderColor: '#facc15',
            backgroundColor: 'rgba(250, 204, 21, 0.15)',
            tension: 0.3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: { suggestedMin: 60, suggestedMax: 180, title: { display: true, text: 'Graus' } },
          x: { title: { display: true, text: 'Frame (relativo ao swing)' } },
        },
      },
    };
    chart = new Chart(chartCanvas, cfg);
  });
</script>

<section class="flex h-full flex-col gap-4 p-4">
  <header class="flex items-center justify-between">
    <a href="#/" class="rounded-md bg-slate-800 px-3 py-1 active:bg-slate-700">‹ Voltar</a>
    <h1 class="text-xl font-semibold">Análise da sessão</h1>
    <span></span>
  </header>

  {#if !session}
    <p class="text-slate-400">Carregando…</p>
  {:else}
    <div class="grid gap-4 md:grid-cols-2">
      <div class="flex flex-col gap-3">
        {#if hasReplay && videoUrl && session?.courtTimeline}
          <!-- Replay: <video> + synchronized mini-court, driven by timeline
               samples captured during live analysis. -->
          <ReplayBlock
            {videoUrl}
            timeline={session.courtTimeline}
            {impacts}
            {contactTimesMs}
          />
        {:else}
          <!-- Legacy fallback for sessions recorded before the replay
               feature landed (no `courtTimeline` persisted). -->
          <div class="min-h-[360px]">
            <MiniQuadra
              {impacts}
              lastEvent={currentImpactForMiniQuadra}
              score={{ p1: matchState.p1Points, p2: matchState.p2Points }}
            />
          </div>
          {#if videoUrl}
            <!-- svelte-ignore a11y_media_has_caption -->
            <video controls src={videoUrl} class="w-full rounded-md bg-black"></video>
          {/if}
        {/if}
        <p class="text-sm text-slate-400">
          {session.swings.length} swings em {(session.durationMs / 1000).toFixed(1)}s @ {session.fps.toFixed(
            0,
          )} fps
        </p>

        {#if session.swings.length === 0}
          <div class="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
            <strong class="block pb-1">Nenhum swing detectado</strong>
            Pode ser que (a) o jogador esteja muito distante da câmera
            (velocidade do pulso em pixels fica abaixo do limiar) ou (b) o
            vídeo é narração/replay em vez de gameplay. Tenta um vídeo com o
            jogador ocupando pelo menos 1/4 da altura do frame.
          </div>
        {/if}

        <Scoreboard bind:state={matchState} />
      </div>

      <div class="flex flex-col gap-2">
        {#if session.swings.length > 0}
          <label class="text-sm text-slate-400">
            Swing
            <select
              class="ml-2 rounded-md bg-slate-800 p-1"
              bind:value={selectedSwing}
            >
              {#each session.swings as _, i}
                <option value={i}>#{i + 1} @ {(_.contactTimeMs / 1000).toFixed(1)}s</option>
              {/each}
            </select>
          </label>
        {/if}

        {#if session.swings[selectedSwing]}
          {@const sw = session.swings[selectedSwing]!}
          <dl class="grid grid-cols-2 gap-2 rounded-md bg-slate-800 p-3 text-sm">
            <dt class="text-slate-400">Ângulo no contato</dt>
            <dd>{sw.elbowAngleAtContactDeg.toFixed(1)}°</dd>
            <dt class="text-slate-400">Vel. angular pico</dt>
            <dd>{sw.peakWristAngularVelocityRadPerSec.toFixed(2)} rad/s</dd>
            <dt class="text-slate-400">Duração do swing</dt>
            <dd>{(sw.swingDurationMs / 1000).toFixed(2)}s</dd>
          </dl>

          <div class="h-64 rounded-md bg-slate-800 p-2">
            <canvas bind:this={chartCanvas}></canvas>
          </div>
        {/if}

        <!-- Coaching section is ALWAYS rendered so the user sees feedback even
             when no individual swing is detected (empty-state pointers). -->
        <section class="rounded-md bg-slate-800 p-3">
          <h3 class="pb-2 text-xs uppercase tracking-wider text-slate-400">
            Dicas do treinador
          </h3>
          {#if session.swings.length === 0}
            <ul class="flex flex-col gap-1 text-sm">
              <li class="flex gap-2">
                <span class="text-racket">•</span>
                <span>Posicione a câmera de modo que o jogador ocupe no mínimo 1/4 da altura da tela.</span>
              </li>
              <li class="flex gap-2">
                <span class="text-racket">•</span>
                <span>Evite vídeos com replay / câmera lenta — confunde a detecção temporal.</span>
              </li>
              <li class="flex gap-2">
                <span class="text-racket">•</span>
                <span>Gravação com tripé dá muito mais recall que câmera na mão.</span>
              </li>
            </ul>
          {:else if feedback.tips.length === 0}
            <p class="text-sm text-slate-500">
              Nenhuma regra foi disparada neste swing — bom sinal, mantenha o padrão.
            </p>
          {:else}
            <ul class="flex flex-col gap-1 text-sm">
              {#each feedback.tips as tip}
                <li class="flex gap-2">
                  <span class="text-racket">•</span>
                  <span>{tip}</span>
                </li>
              {/each}
            </ul>
          {/if}
        </section>
      </div>
    </div>
  {/if}
</section>
